import { useState, type KeyboardEvent } from "react";
import * as Popover from "@radix-ui/react-popover";
import type { Editor } from "@tiptap/react";
import { Link as LinkIcon, Unlink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ToolbarButton } from "./ToolbarButton";

// "ornek.com" gibi şemasız adreslere https:// ekler; göreli yol, çapa,
// mailto: ve tel: olduğu gibi kalır.
function normalizeUrl(raw: string): string {
  const url = raw.trim();
  if (!url) return "";
  if (/^(?:[a-z][a-z\d+.-]*:|\/|#|\?)/i.test(url)) return url;
  return `https://${url}`;
}

// Link ekle / düzenle / kaldır. Seçim yokken URL'nin kendisi link metni olarak eklenir.
export function LinkPopover({ editor, active }: { editor: Editor; active: boolean }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [newTab, setNewTab] = useState(false);

  const onOpenChange = (next: boolean) => {
    if (next) {
      const attrs = editor.getAttributes("link");
      setUrl(typeof attrs.href === "string" ? attrs.href : "");
      setNewTab(attrs.target === "_blank");
    }
    setOpen(next);
  };

  const remove = () => {
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    setOpen(false);
  };

  const apply = () => {
    const href = normalizeUrl(url);
    if (!href) {
      remove();
      return;
    }
    const attrs = {
      href,
      target: newTab ? "_blank" : null,
      rel: newTab ? "noopener noreferrer" : null,
    };
    const chain = editor.chain().focus();
    if (editor.state.selection.empty && !editor.isActive("link")) {
      chain
        .insertContent({ type: "text", text: href, marks: [{ type: "link", attrs }] })
        .unsetMark("link")
        .run();
    } else {
      chain.extendMarkRange("link").setLink(attrs).run();
    }
    setOpen(false);
  };

  // Enter formu göndermesin, linki uygulasın.
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      apply();
    }
  };

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger asChild>
        <ToolbarButton label={active ? "Linki düzenle" : "Link ekle"} active={active}>
          <LinkIcon />
        </ToolbarButton>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          onCloseAutoFocus={(e) => e.preventDefault()}
          className="z-50 w-80 space-y-3 rounded-md border border-border bg-popover p-3 text-popover-foreground shadow-md"
        >
          <div className="space-y-1">
            <label htmlFor="rte-link-url" className="text-xs font-medium">
              Bağlantı adresi
            </label>
            <Input
              id="rte-link-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="https://ornek.com"
              className="h-8"
              autoFocus
            />
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={newTab}
              onChange={(e) => setNewTab(e.target.checked)}
              className="size-3.5 accent-primary"
            />
            Yeni sekmede aç
          </label>
          <div className="flex items-center justify-between gap-2">
            {active ? (
              <Button type="button" size="sm" variant="ghost" className="h-7 text-destructive" onClick={remove}>
                <Unlink className="size-3.5" />
                Linki kaldır
              </Button>
            ) : (
              <span />
            )}
            <Button type="button" size="sm" className="h-7" onClick={apply}>
              Uygula
            </Button>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
